import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteThirdPartyApps } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { ThirdPartyApp } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteThirdPartyAppsDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedApps: ThirdPartyApp[];
  changeLogRoles: string[];
}

export default function DeleteThirdPartyAppsDialog({
  open,
  handleClose,
  userEmail,
  selectedApps,
  changeLogRoles,
}: DeleteThirdPartyAppsDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteThirdPartyApps(userEmail, selectedApps, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Apps deleted successfully!',
      });

      queryClient.setQueryData(
        [UserKeys.ThirdPartyApps, userEmail],
        (oldData: ThirdPartyApp[]) => {
          return oldData.filter((app) => !selectedApps.includes(app));
        }
      );

      setChangeLogMessage('');

      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue deleting the app(s). Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.third_party_apps
          .delete_third_party_apps,
        {
          module: ModuleList.modules.userManagement.name,
          section:
            ModuleList.modules.userManagement.sections.thirdPartyApplications,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.RevokeAuthTokens.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={
        selectedApps.length > 1
          ? 'Delete Third Party Apps'
          : 'Delete Third Party App'
      }
      dialogContent={
        <Box>
          <Typography sx={{ mb: 2 }}>
            {selectedApps.length > 1 ? (
              <>
                Delete <strong>{selectedApps.length} apps</strong> from{' '}
                <strong>{userEmail}'s</strong> third party apps?
              </>
            ) : (
              <>
                Delete <strong>{selectedApps[0]?.displayText}</strong> from{' '}
                <strong>{userEmail}'s</strong> third party apps?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: selectedApps.length > 1 ? 'Delete Apps' : 'Delete App',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
